import { useCallback } from 'react';

import _ from 'lodash';

import { getConditionalObject } from 'utils/data';

import { useOpenCicFieldReasonsModal } from '../useOpenCicFieldReasonsModal';
import { useOpenCicSaveReasonModal } from '../useOpenCicSaveReasonModal';

/**
 * Asks the user why a save changed CIC trigger fields and fees, and builds the re-save request. The pop-up lists
 * `cicReasonChanges` (per-field mode) and `cicFeeChanges`; single-note mode asks for its note first, in the legacy
 * modal. Resolves with `previousRequest` plus `cicReasons`, `cicFeeReasons` and/or the legacy `cicReason`, or `null`
 * when the user cancels. The pop-up is pre-filled from `previousRequest`, the previous attempt.
 */
export const useRequestCicReasons = () => {
  const openCicFieldReasonsModal = useOpenCicFieldReasonsModal();
  const openCicSaveReasonModal = useOpenCicSaveReasonModal();

  return useCallback(
    async (metadata, previousRequest) => {
      const hasFieldChanges = !_.isEmpty(metadata?.cicReasonChanges);
      const hasFeeChanges = !_.isEmpty(metadata?.cicFeeChanges);
      const isLegacyNoteNeeded =
        !hasFieldChanges && (!hasFeeChanges || !_.isEmpty(metadata?.cicReasonTriggerFields));

      const cicReason = isLegacyNoteNeeded
        ? await openCicSaveReasonModal(metadata?.cicReasonTriggerFields)
        : null;

      if (isLegacyNoteNeeded && !cicReason) {
        return null;
      }

      const legacyNoteData = getConditionalObject(isLegacyNoteNeeded, { cicReason });

      if (!hasFieldChanges && !hasFeeChanges) {
        return { ...previousRequest, ...legacyNoteData };
      }

      const reasons = await openCicFieldReasonsModal({
        changes: metadata.cicReasonChanges ?? [],
        recalculatedFields: metadata.cicRecalculatedFields,
        saveMessage: metadata.saveMessage,
        previousCicReasons: previousRequest?.cicReasons,
        feeChanges: metadata.cicFeeChanges,
        previousCicFeeReasons: previousRequest?.cicFeeReasons,
      });

      if (!reasons) {
        return null;
      }

      return {
        ...previousRequest,
        ...legacyNoteData,
        ...getConditionalObject(hasFieldChanges, { cicReasons: reasons.cicReasons }),
        ...getConditionalObject(hasFeeChanges, { cicFeeReasons: reasons.cicFeeReasons }),
      };
    },
    [openCicFieldReasonsModal, openCicSaveReasonModal],
  );
};
